/**
 * 디자인 PPT(자유 디자인) API — /api/ppt/free-design
 *
 * 로그인 사용자는 계정으로, 비로그인 사용자는 만들 때 받은 수정 키로 덱을 연다.
 * 수정 키는 개요(대화·라이브러리·나눔터에 복사됨)에 넣지 않고 이 브라우저에만 둔다.
 */

import { getAuth } from 'firebase/auth';
import { getApiBaseUrl } from '../utils/apiConfig';
import { readSseJson } from '../utils/sseJsonStream';

const KEY_STORE = 'whatquiz_fd_keys';
const MAX_KEYS = 30;
const STALL_MS = 90_000;

export class FreeDesignError extends Error {
  constructor(message, { code = 'error', status = 0, currentRevision = null } = {}) {
    super(message);
    this.name = 'FreeDesignError';
    this.code = code;
    this.status = status;
    this.currentRevision = currentRevision;
  }
}

const readKeys = () => {
  try {
    const parsed = JSON.parse(localStorage.getItem(KEY_STORE) || '{}');
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch (_error) {
    return {};
  }
};

export const rememberEditKey = (deckId, key) => {
  if (!deckId || !key) return;
  try {
    const keys = readKeys();
    keys[deckId] = { key, at: Date.now() };
    const trimmed = Object.entries(keys)
      .sort((a, b) => (b[1]?.at || 0) - (a[1]?.at || 0))
      .slice(0, MAX_KEYS);
    localStorage.setItem(KEY_STORE, JSON.stringify(Object.fromEntries(trimmed)));
  } catch (_error) {
    // 저장 공간이 막혀도 로그인 사용자는 계정으로 열 수 있다.
  }
};

const editKeyOf = (deckId) => readKeys()[deckId]?.key || null;

const headersFor = async (deckId = null) => {
  const headers = { 'Content-Type': 'application/json' };
  try {
    const user = getAuth().currentUser;
    if (user) headers.Authorization = `Bearer ${await user.getIdToken()}`;
  } catch (_error) {
    // 비로그인
  }
  const key = deckId ? editKeyOf(deckId) : null;
  if (key) headers['X-Free-Design-Key'] = key;
  return headers;
};

const errorFrom = async (response) => {
  let detail = null;
  try {
    const data = await response.json();
    detail = data?.detail && typeof data.detail === 'object' ? data.detail : { message: data?.detail };
  } catch (_error) {
    detail = null;
  }
  const message = typeof detail?.message === 'string' && /[가-힣]/.test(detail.message)
    ? detail.message
    : '잠시 후 다시 시도해 주세요.';
  return new FreeDesignError(message, {
    code: detail?.code || `http_${response.status}`,
    status: response.status,
    currentRevision: detail?.current_revision ?? null,
  });
};

const base = () => `${getApiBaseUrl()}/api/ppt/free-design`;

// 응답이 시작된 뒤 스트림이 끊기면(화면 이동·새로고침·네트워크) 서버는 끝까지 만들어 저장한다.
// 브라우저가 던지는 TypeError 를 일반 실패로 두면 화면이 「이어받기」 대신 실패로 끝난다.
const readUntilClosed = async (response, signal, onEvent, closedMessage) => {
  try {
    await readEvents(response, onEvent);
  } catch (err) {
    if (err instanceof FreeDesignError || signal?.aborted) throw err;
    throw new FreeDesignError(closedMessage, { code: 'stream_closed' });
  }
};

/**
 * SSE로 덱을 만든다. onEvent 로 progress / notice / art / slide / complete 를 받는다.
 * 완료되면 complete 이벤트의 deck 을 돌려준다. 이번에 새 본보기를 읽었으면 deck.design_reference 에 함께 싣는다.
 */
export async function generateFreeDesign({
  outline, requestText = '', conversationId = null, referenceRequest = '', referenceFileIds = [], signal, onEvent,
}) {
  const response = await fetch(`${base()}/generate`, {
    method: 'POST',
    headers: await headersFor(),
    body: JSON.stringify({
      outline,
      request_text: String(requestText || '').slice(-20000),
      conversation_id: conversationId || undefined,
      ...(referenceFileIds.length ? {
        reference_request: String(referenceRequest || '').slice(0, 2000),
        reference_file_ids: referenceFileIds.slice(0, 20),
      } : {}),
    }),
    signal,
  });
  if (!response.ok || !response.body) throw await errorFrom(response);

  let finalDeck = null;
  const closedMessage = '연결이 끊겨 PPT를 끝까지 만들지 못했어요. 다시 시도해 주세요.';
  await readUntilClosed(response, signal, (event) => {
    if (event.type === 'error') {
      throw new FreeDesignError(event.message || 'PPT를 만들지 못했어요.', { code: event.error_code || 'generation_failed' });
    }
    if (event.type === 'complete') {
      finalDeck = event.deck && event.design_reference ? { ...event.deck, design_reference: event.design_reference } : event.deck;
      if (finalDeck?.edit_key) rememberEditKey(finalDeck.deck_id, finalDeck.edit_key);
    }
    onEvent?.(event);
  }, closedMessage);
  if (!finalDeck) {
    if (signal?.aborted) {
      const abort = new Error('Aborted');
      abort.name = 'AbortError';
      throw abort;
    }
    throw new FreeDesignError(closedMessage, { code: 'stream_closed' });
  }
  const { edit_key: _omit, ...deck } = finalDeck;
  return deck;
}

/** SSE 본문을 끝까지 읽으며 heartbeat 를 뺀 이벤트를 handle 에 넘긴다. 오래 조용하면 끊는다. */
async function readEvents(response, handle) {
  for await (const event of readSseJson(response.body, { idleTimeoutMs: STALL_MS, idleTimeoutError: null })) {
    if (event.type === 'heartbeat') continue;
    handle(event);
  }
}

/** 나눔터에서 가져온 텍스트형 덱을 내 덱으로 복사한다. */
export async function cloneSharedFreeDesign(galleryItemId, { signal } = {}) {
  const response = await fetch(`${base()}/clone-shared`, {
    method: 'POST',
    headers: await headersFor(),
    body: JSON.stringify({ gallery_item_id: galleryItemId }),
    signal,
  });
  if (!response.ok) throw await errorFrom(response);
  const { deck } = await response.json();
  rememberEditKey(deck?.deck_id, deck?.edit_key);
  const { edit_key: _key, ...publicDeck } = deck || {};
  return publicDeck;
}

export async function loadFreeDesign(deckId, { signal } = {}) {
  const response = await fetch(`${base()}/${encodeURIComponent(deckId)}`, {
    headers: await headersFor(deckId),
    signal,
  });
  if (!response.ok) throw await errorFrom(response);
  const data = await response.json();
  return data.deck;
}

/** 이 대화에서 내가 가장 최근에 만든 덱 요약({ deck_id, created_at, ... }) 또는 null. 로그인 사용자만. */
export async function latestFreeDesign(conversationId, { since = 0, signal } = {}) {
  const params = new URLSearchParams({ conversation_id: conversationId, since: String(Math.max(0, since)) });
  const response = await fetch(`${base()}/latest?${params}`, { headers: await headersFor(), signal });
  if (!response.ok) throw await errorFrom(response);
  const data = await response.json();
  return data.deck || null;
}

export async function saveFreeDesign(deckId, { baseRevision, slides, title, keepalive = false }) {
  const response = await fetch(`${base()}/${encodeURIComponent(deckId)}`, {
    method: 'PUT',
    headers: await headersFor(deckId),
    body: JSON.stringify({ base_revision: baseRevision, slides, title }),
    keepalive,
  });
  if (!response.ok) throw await errorFrom(response);
  return response.json();
}

export async function redesignFreeDesignSlide(deckId, {
  baseRevision, slideId, slideIndex, outline, instruction = '', requestText = '', keepText = false, signal,
}) {
  const response = await fetch(`${base()}/${encodeURIComponent(deckId)}/redesign`, {
    method: 'POST',
    headers: await headersFor(deckId),
    body: JSON.stringify({
      base_revision: baseRevision,
      slide_id: slideId,
      slide_index: slideIndex,
      outline,
      instruction: String(instruction || '').slice(0, 2000),
      request_text: String(requestText || '').slice(-20000),
      keep_text: Boolean(keepText),
    }),
    signal,
  });
  if (!response.ok) throw await errorFrom(response);
  return response.json();
}

/**
 * 채팅 수정으로 바뀐 여러 장을 한꺼번에 다시 디자인한다 (SSE). onEvent 로 progress({done,total})를 받고
 * complete 의 { revision, slides, failed } 를 돌려준다. 연결이 끊겨도 서버는 끝까지 만들어 저장한다.
 */
export async function redesignFreeDesignSlides(deckId, {
  baseRevision, items, outline, requestText = '', outlineSig = '', slideSigs = [], signal, onEvent,
}) {
  const response = await fetch(`${base()}/${encodeURIComponent(deckId)}/redesign-batch`, {
    method: 'POST',
    headers: await headersFor(deckId),
    body: JSON.stringify({
      base_revision: baseRevision,
      items: items.map(({ slideId, slideIndex }) => ({ slide_id: slideId, slide_index: slideIndex })),
      outline,
      request_text: String(requestText || '').slice(-20000),
      outline_sig: outlineSig,
      slide_sigs: slideSigs,
    }),
    signal,
  });
  if (!response.ok || !response.body) throw await errorFrom(response);
  let result = null;
  const closedMessage = '연결이 끊겼어요. 서버에서 마저 반영하고 있으니 잠시 후 다시 열어 주세요.';
  await readUntilClosed(response, signal, (event) => {
    if (event.type === 'error') {
      throw new FreeDesignError(event.message || '채팅 내용을 반영하지 못했어요.', {
        code: event.error_code || 'redesign_failed',
        status: event.error_code === 'conflict' ? 409 : 0,
      });
    }
    if (event.type === 'complete') result = event;
    onEvent?.(event);
  }, closedMessage);
  if (!result) {
    throw new FreeDesignError(closedMessage, { code: 'stream_closed' });
  }
  return { revision: result.revision, slides: result.slides || [], failed: result.failed || [] };
}

export async function exportFreeDesign(deckId, format, { signal } = {}) {
  const response = await fetch(`${base()}/${encodeURIComponent(deckId)}/export`, {
    method: 'POST',
    headers: await headersFor(deckId),
    body: JSON.stringify({ format }),
    signal,
  });
  if (!response.ok) throw await errorFrom(response);
  const data = await response.json();
  if (!data?.download_url) throw new FreeDesignError('파일을 만들지 못했어요. 다시 시도해 주세요.', { code: 'empty_url' });
  return { url: data.download_url, filename: data.filename, editable: data.editable === true };
}
